import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { buildTipTapDocument } from 'pages/ApplicationDetailsPage/components/Essay/utils/tiptapContent'
import s from './styles/LorRichText.module.scss'

const LorRichText = forwardRef(
	({ letterId, contentJson, contentText, isEditing, className = '' }, ref) => {
		const lastSyncedRef = useRef('')

		const editor = useEditor({
			extensions: [StarterKit],
			content: buildTipTapDocument(contentJson, contentText),
			editable: Boolean(isEditing),
			editorProps: {
				attributes: {
					class: s.editor,
				},
			},
		})

		useEffect(() => {
			if (!editor) return
			editor.setEditable(Boolean(isEditing))
		}, [editor, isEditing])

		useEffect(() => {
			if (!editor || isEditing) return
			const signature = JSON.stringify({ letterId, contentJson, contentText })
			if (signature === lastSyncedRef.current) return
			lastSyncedRef.current = signature
			editor.commands.setContent(buildTipTapDocument(contentJson, contentText), false)
		}, [editor, isEditing, letterId, contentJson, contentText])

		useImperativeHandle(
			ref,
			() => ({
				getSnapshot: () => {
					if (!editor) {
						return {
							contentJson: contentJson || null,
							contentHtml: '',
							contentText: contentText || '',
							wordCount: 0,
						}
					}
					const text = editor.getText()
					return {
						contentJson: editor.getJSON(),
						contentHtml: editor.getHTML(),
						contentText: text,
						wordCount: text.trim() ? text.trim().split(/\s+/).length : 0,
					}
				},
			}),
			[contentJson, contentText, editor],
		)

		return (
			<div className={`${s.root} ${isEditing ? s.editing : ''} ${className}`}>
				{isEditing ? (
					<div className={s.toolbar}>
						<button
							type="button"
							className={s.tool}
							onClick={() => editor?.chain().focus().toggleBold().run()}
						>
							Bold
						</button>
						<button
							type="button"
							className={s.tool}
							onClick={() => editor?.chain().focus().toggleItalic().run()}
						>
							Italic
						</button>
						<button
							type="button"
							className={s.tool}
							onClick={() => editor?.chain().focus().toggleBulletList().run()}
						>
							List
						</button>
					</div>
				) : null}
				<EditorContent editor={editor} />
			</div>
		)
	},
)

LorRichText.displayName = 'LorRichText'

export default LorRichText
